import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Target, TrendingUp, Users, Zap, ShieldCheck, Star, Rocket, ListChecks, MessageCircle, DollarSign, FileText, CheckCircle2 } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Criar um Plano de Vendas para IPTV", href: "/blog/como-criar-plano-vendas-iptv" }
  ];

  const sections = [
    {
      title: "Plano de Vendas: O Mapa do seu Tesouro Digital",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            Vender IPTV em 2026 exige mais do que apenas "postar e esperar". Os revendedores que faturam acima de R$ 10 mil mensais possuem algo em comum: um <strong>plano de vendas estruturado</strong>. Esse plano é o documento (ou mentalidade) que define quem você vai atingir, como vai falar com eles e qual a meta de fechamento diária para atingir seus objetivos com o <strong>painel P2 PRO</strong>.
          </p>
          <p>
            Um bom plano de vendas transforma o caos da prospecção em uma rotina previsível de lucro. Ao alinhar seus canais de aquisição (Instagram, TikTok, Google) com uma oferta irresistível baseada na tecnologia da <strong>P2 PRO</strong>, você para de "tentar vender" e passa a "gerir pedidos".
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-10 rounded-r-3xl relative overflow-hidden">
            <h4 className="text-white font-black mb-2 uppercase italic text-xl">Dica de Estratégia</h4>
            <p className="italic text-white/90">
              "Um plano de vendas sem metas é apenas um desejo. Defina exatamente quantos novos assinantes você quer por semana e trabalhe o funil para chegar lá."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Os 5 Pilares do Plano de Vendas Master",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "Definição de Público", d: "Saiba exatamente para quem você vende: fãs de esportes, famílias com crianças ou amantes de cinema.", i: <Target /> },
            { t: "Canais de Aquisição", d: "Escolha onde você vai investir tempo: Tráfego Orgânico (Reels/TikTok) ou Pago (Google Ads).", i: <Rocket /> },
            { t: "Script de Fechamento", d: "Tenha mensagens prontas que quebram objeções e conduzem o lead do teste para o pagamento.", i: <MessageCircle /> },
            { t: "Estratégia de Pricing", d: "Defina seus pacotes: Mensal, Trimestral e Anual. O foco deve ser sempre na recorrência e no LTV.", i: <DollarSign /> },
            { t: "Funil de Conversão", d: "Mensure quantos cliques geram um teste e quantos testes geram uma venda paga.", i: <TrendingUp /> },
            { t: "Follow-up Ativo", d: "Crie um processo para falar com quem testou e não comprou. O dinheiro está no acompanhamento.", i: <ListChecks /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Executando o Plano no Dia a Dia",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            A execução é o que separa o plano no papel do dinheiro no bolso. Utilize o <strong>sistema P2 PRO</strong> para facilitar a entrega dos testes prometidos no seu plano. Se o seu plano diz "gerar 10 testes por dia", você precisa de fontes de tráfego que entreguem esses interessados. Mantenha um CRM (mesmo que simples, como etiquetas no WhatsApp) para nunca perder um lead de vista.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <CheckCircle2 className="text-electric w-8 h-8" /> Checklist da Execução Master
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <FileText className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Meta Diária:</span> Quantos novos contatos você abordou hoje? O crescimento exige prospecção ativa.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Zap className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Velocidade de Resposta:</span> No IPTV, quem responde primeiro geralmente leva a venda. Automatize o básico.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Star className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Oferta de Valor:</span> Não venda apenas canais. Venda o acesso aos apps One X, os mais bonitos do mercado.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Um plano de vendas bem feito para sua revenda de IPTV é o que garante que você não será apenas mais um no mercado. Unindo essa estratégia comercial com a estabilidade de sinal e o suporte de elite da <strong>P2 PRO</strong>, você constrói um negócio previsível, lucrativo e altamente escalável.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Como Revender Iptv"
      title="Como Criar um Plano de Vendas para IPTV: Guia 2026"
      subtitle="O segredo dos revendedores que faturam alto. Aprenda a estruturar suas metas, canais e scripts para vender todos os dias."
      heroImage="https://images.unsplash.com/photo-1454165205744-3b78555e5572?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitos revendedores falham porque não têm um plano. Eles postam um story hoje, esquecem amanhã e reclamam que as vendas não acontecem. Em 2026, o mercado de streaming exige profissionalismo comercial. Se você quer escalar, precisa de um plano de vendas claro e executável.",
        "Neste artigo, vamos te ensinar a construir o seu próprio plano de vendas, do zero. Vamos falar sobre como definir seu público-alvo, quais os melhores canais para prospectar leads qualificados e como estruturar scripts de vendas que convertem até o cliente mais cético.",
        "Combinando uma estratégia comercial sólida com o produto premium da P2 PRO, você terá em mãos tudo o que precisa para dominar o seu nicho e garantir uma renda recorrente crescente mês após mês."
      ]}
      benefits={[
        { title: "Previsibilidade Financeira", description: "Saiba exatamente quanto você vai faturar com base no volume de leads que gera." },
        { title: "Foco e Produtividade", description: "Pare de perder tempo com estratégias que não funcionam e foque no que traz lucro real." },
        { title: "Escalabilidade Facilitada", description: "Um processo de vendas documentado é muito mais fácil de ser delegado ou ampliado." },
        { title: "Redução de Objeções", description: "Um plano bem estruturado já antecipa as dúvidas do cliente, facilitando o fechamento." }
      ]}
      howItWorks={[
        { title: "Diagnóstico de Metas", description: "Defina quanto você quer ganhar e quantos clientes precisa para atingir esse valor." },
        { title: "Seleção de Canais", description: "Escolha as 2 ou 3 redes sociais onde seu público mais consome entretenimento." },
        { title: "Criação de Ofertas", description: "Estruture seus planos (Mensal, Anual) com gatilhos de urgência e escassez." },
        { title: "Ajuste e Otimização", description: "Analise seus resultados semanalmente e ajuste o plano conforme o feedback do mercado." }
      ]}
      diferenciais={[
        { title: "Apps de Luxo One X", description: "Inclua no seu plano a demonstração visual dos nossos apps, um diferencial imbatível." },
        { title: "Sinal P2P Inabalável", description: "Venda com a segurança de que o produto que você entrega é o melhor do mercado." },
        { title: "Apoio Master P2 PRO", description: "Conte com nossa experiência para validar seu script de vendas e aumentar sua conversão." }
      ]}
      faq={[
        { question: "O que não pode faltar em um plano de vendas?", answer: "Metas claras, definição de público e scripts de fechamento validados." },
        { question: "Qual a melhor meta para quem está começando?", answer: "Recomendamos focar em conseguir 1 novo cliente pagante por dia no primeiro mês." },
        { question: "Como lidar com quem pede desconto?", answer: "No seu plano, tenha sempre uma oferta de 'Plano Anual' que já oferece um desconto por tempo de permanência." },
        { question: "O plano de vendas serve para tráfego orgânico?", answer: "Com certeza. O plano define o que você vai postar para atrair os leads sem gastar com anúncios." },
        { question: "Como saber se o meu plano está funcionando?", answer: "A métrica final é o dinheiro no bolso, mas acompanhe também o número de testes gerados diariamente." },
        { question: "Devo mudar meu plano toda semana?", answer: "Não. Teste um plano por pelo menos 30 dias antes de fazer mudanças drásticas na estratégia." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Estruture seu Sucesso com a P2 PRO"
      ctaDescription="Combine o melhor plano de vendas com o melhor produto do mercado. Seja um Master de elite hoje."
      ctaButtonText="Ser um Revendedor Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-criar-plano-vendas-iptv")({
  head: () => ({
    meta: [
      { title: "Como Criar um Plano de Vendas para IPTV: Guia Master 2026 | P2 PRO" },
      { name: "description", content: "Aprenda a estruturar um plano de vendas vencedor para sua revenda de IPTV. Dicas de metas, canais e scripts de fechamento para faturar alto." },
      { name: "keywords", content: "plano de vendas iptv, marketing de revenda, como vender streaming, estratégia comercial iptv" }
    ],
  }),
  component: ArticlePage,
});